import React from "react";
import { motion } from "framer-motion";
import { Palette, Cpu, Zap } from "lucide-react";

const cards = [
  {
    icon: Palette,
    tag: "Creative",
    title: "Cinematic Look Development",
    desc: "Hyper-real worlds, mythic realism, creature concepts, character look books, grounded action beats, and premium film-grade visual storytelling.",
  },
  {
    icon: Cpu,
    tag: "Technical",
    title: "AI-VFX Shot Design",
    desc: "Prompt systems, image-to-image continuity, identity-locked references, camera/lens preservation, lighting continuity, and VFX integration logic.",
  },
  {
    icon: Zap,
    tag: "Production",
    title: "Fast Decision Support",
    desc: "Director requirement explorations, current/future schedule ideation, edit placeholders, shot breakdowns, and production coordination under pressure.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function ProfileSection() {
  return (
    <section
      id="profile"
      data-testid="profile-section"
      className="py-24 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="section-number mb-4">01 &middot; Profile</p>
          <h2
            className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-none text-white max-w-3xl"
            data-testid="profile-heading"
          >
            Built for the space between{" "}
            <span className="font-serif-accent text-amber-400">
              director intent
            </span>{" "}
            and final frame.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-neutral-400 leading-relaxed max-w-2xl">
            AI Artist and AI-VFX creative lead focused on photoreal cinematic
            frames, director-facing ideation, set extension logic, full-sequence
            AI previz, and production-friendly visual communication.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {cards.map((card, i) => (
            <motion.div
              key={card.title}
              custom={i}
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-50px" }}
              data-testid={`profile-card-${i}`}
              className="glass-surface glass-surface-hover rounded-2xl p-8 transition-all duration-500 group"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <card.icon size={20} className="text-amber-500" />
                </div>
                <span className="text-xs font-mono tracking-[0.15em] uppercase text-amber-500/80">
                  {card.tag}
                </span>
              </div>
              <h3 className="font-heading text-xl tracking-tight text-white mb-3">
                {card.title}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                {card.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
